import { Fragment, useEffect, useMemo, useState, useCallback } from "react";
import { useApiClient } from "../api/useApiClient";
import type { ReviewItem } from "../types/reviewItem";
import { StatusBadge } from "../components/StatusBadge";
import { LineasRow, LineasToggle } from "../components/LineasRow";
import { CfeIdentidad } from "../components/CfeIdentidad";
import { TablaToolbar } from "../components/TablaToolbar";
import { Paginador } from "../components/Paginador";
import { ThOrdenable } from "../components/ThOrdenable";
import { abrirEnNuevaPestana } from "../utils/abrirEnNuevaPestana";
import { formatMonto, formatFecha } from "../utils/formato";
import { useTablaBandeja } from "../hooks/useTablaBandeja";
import { useAccion } from "../hooks/useAccion";
import { AvisoError } from "../components/AvisoError";
import { useContadores } from "../contexts/ContadoresContext";

// [FUSIONADAS 10/09/2026] Antes 9, con "Acciones" (Reintentar) en columna
// aparte. Ver el comentario del <td> "actions-cell" más abajo.
const COLUMNAS = 8;

/**
 * [NUEVO 27/08/2026] Filtro de estado dentro de la propia bandeja combinada.
 * Antes de esto, "Error de posteo" quedaba mezclado entre las ~600+ filas de
 * "En SAP" sin ninguna forma de aislarlo salvo Ctrl+F visual — pedido de
 * Liber Almeida tras revisar la bandeja real. Es un filtro 100% client-side
 * (los tres arrays ya están cargados en `items`) — no agrega ningún llamado
 * nuevo a la API.
 */
type FiltroEstado = "todos" | "pendiente" | "en_sap" | "error_posteo";

const TABS_ESTADO: Array<{ clave: FiltroEstado; etiqueta: string }> = [
  { clave: "todos", etiqueta: "Todos" },
  { clave: "pendiente", etiqueta: "En cola de posteo" },
  { clave: "en_sap", etiqueta: "En SAP" },
  { clave: "error_posteo", etiqueta: "Error de posteo" },
];

function coincideFiltroEstado(item: ReviewItem, filtro: FiltroEstado): boolean {
  switch (filtro) {
    case "pendiente":
      return item.status === "APROBADO_PENDIENTE_POSTEO" || item.status === "PROCESANDO";
    case "en_sap":
      return item.status === "EN_SAP";
    case "error_posteo":
      return item.status === "ERROR_POSTEO";
    default:
      return true;
  }
}

/**
 * [AMPLIADA 11/08/2026] Muestra los TRES estados del posteo:
 * APROBADO_PENDIENTE_POSTEO (+ PROCESANDO), EN_SAP y ERROR_POSTEO.
 *
 * El primero se sumó porque era el único estado del pipeline invisible en la
 * UI: una vez que el Aprobador aprobaba, Contaduría no tenía forma de seguir
 * la factura. Se había excluido con buen criterio (con el Timer cada 2 minutos
 * era un estado de tránsito de segundos), pero con `post_to_sap` deshabilitado
 * lo aprobado se queda ahí indefinidamente.
 *
 * Va en ESTA pantalla y no en una nueva: es literalmente la pantalla del
 * posteo, y el dato que faltaba —de quién viene / a quién espera— es una
 * columna, no una pantalla. La columna Estado ya distinguía los estados y
 * StatusBadge ya tenía las etiquetas de los dos nuevos.
 *
 * Originalmente mostraba EN_SAP + ERROR_POSTEO juntas. El backend separó esos dos filtros
 * (ver CLAUDE.md) — `en_sap` ya NO incluye ERROR_POSTEO — así que acá se
 * traen ambos y se combinan client-side para mantener la vista combinada.
 * "Reintentar" solo debería ofrecerse a Contaduría (mismo criterio que
 * reabrir) — el backend ya lo exige por rol, acá solo se oculta el botón
 * como control blando adicional, no como única barrera.
 *
 * A diferencia de las bandejas de aprobación, acá la columna Estado SÍ se
 * mantiene: esta pantalla mezcla dos estados y además lleva el badge de
 * "Detectado por reconciliación".
 *
 * [NUEVO 27/08/2026] Tabs de filtro por estado (Todos/En cola/En SAP/Error de
 * posteo), client-side sobre `items` ya cargado — no agrega llamados a la
 * API. Antes de esto, "Error de posteo" no tenía forma de aislarse entre las
 * ~600+ filas de "En SAP".
 */
export function PosteoPage() {
  const api = useApiClient();
  const { refrescar: refrescarContadores } = useContadores();
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstado>("todos");

  const itemsFiltrados = useMemo(
    () => items.filter((item) => coincideFiltroEstado(item, filtroEstado)),
    [items, filtroEstado]
  );

  // [AJUSTADO 01/09/2026] Las cantidades de los tabs se calculan una sola vez
  // por cambio de `items`, en una pasada, en vez de recorrer las ~600 filas
  // por cada tab en cada render. Reusa el mismo predicado que el filtro real,
  // para que el número del tab no pueda divergir de lo que muestra.
  //
  // OJO: son cantidades GLOBALES, no acotadas por el buscador — a diferencia
  // del contador del TablaToolbar de abajo, que sí lo respeta.
  const cantidadesPorTab = useMemo(() => {
    const conteo = Object.fromEntries(TABS_ESTADO.map((t) => [t.clave, 0])) as Record<
      FiltroEstado,
      number
    >;
    for (const item of items) {
      for (const { clave } of TABS_ESTADO) {
        if (coincideFiltroEstado(item, clave)) conteo[clave]++;
      }
    }
    return conteo;
  }, [items]);

  const tabla = useTablaBandeja(itemsFiltrados, {
    // `comprador_asignado` entra a la búsqueda: con esta pantalla mostrando ya
    // el estado del posteo, buscar por aprobador es justo lo que se pidió.
    camposBusqueda: ["proveedor_nombre", "rut_emisor", "nro", "serie", "comprador_asignado"],
    ordenInicial: { columna: "fecha_documento", direccion: "desc" },
    camposNumericos: ["monto"],
    claveEstable: "id",
  });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [enCola, enSap, errorPosteo] = await Promise.all([
        api.listarReviewItems("contaduria", "aprobado_pendiente_posteo"),
        api.listarReviewItems("contaduria", "en_sap"),
        api.listarReviewItems("contaduria", "error_posteo"),
      ]);
      setItems([...enCola, ...enSap, ...errorPosteo]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const recargarTodo = useCallback(async () => {
    await cargar();
    refrescarContadores();
  }, [cargar, refrescarContadores]);

  const accion = useAccion(recargarTodo);

  function toggleExpandido(id: string) {
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Via `accion.ejecutar`: refresca SIEMPRE y muestra el error sin destruir
  // la tabla. Ver useAccion.ts y el incidente del 10/08/2026.
  async function reintentar(id: string) {
    await accion.ejecutar(() => api.reintentarPosteo(id), {
      descripcion: "reintentar el posteo",
    });
  }

  async function abrirDocumento(id: string, tipo: "xml" | "pdf" | "adjunto") {
    try {
      const url = await api.obtenerBlobSasUrl(id, tipo);
      abrirEnNuevaPestana(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`No se pudo obtener el ${tipo.toUpperCase()}:`, err);
      window.alert(`No se pudo abrir el ${tipo.toUpperCase()}. Ver consola para el detalle.`);
    }
  }

  if (loading) return <p>Cargando...</p>;

  return (
    <div>
      <h1>Posteo a SAP</h1>
      <p className="subtitle">
        Seguimiento de lo ya aprobado: en cola de posteo, posteado en SAP, o con error.
      </p>

      <div className="filtro-tabs" role="group" aria-label="Filtrar por estado del posteo">
        {TABS_ESTADO.map(({ clave, etiqueta }) => (
          <button
            key={clave}
            type="button"
            // Botón de alternancia, NO role="tab": no cambia entre paneles de
            // contenido, filtra una lista. Marcarlo como tab prometería
            // navegación por flechas y un tabpanel que no existen.
            aria-pressed={filtroEstado === clave}
            className={`filtro-tab${filtroEstado === clave ? " filtro-tab-activo" : ""}`}
            onClick={() => setFiltroEstado(clave)}
          >
            {etiqueta} <span className="filtro-tab-cantidad">{cantidadesPorTab[clave]}</span>
          </button>
        ))}
      </div>

      <TablaToolbar
        busqueda={tabla.busqueda}
        onBuscar={tabla.setBusqueda}
        filtradas={tabla.filtradas}
        totalSinFiltro={itemsFiltrados.length}
      />

      <AvisoError mensaje={accion.error} onCerrar={accion.limpiarError} />

      <table className="compact">
        {/* [AJUSTADO 10/09/2026] Se sacó la columna "Acciones" (4%) al
            fusionar Reintentar dentro de "Documentos", que ahora puede
            mostrar hasta 5 botones (XML/PDF/Adjunto/Líneas/Reintentar) y por
            eso subió de 8% a 12%. Suma 100%: 15+15+10+8+14+13+13+12. */}
        <colgroup>
          <col style={{ width: "15%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "8%" }} />
          <col style={{ width: "14%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "13%" }} />
          <col style={{ width: "12%" }} />
        </colgroup>
        <thead>
          <tr>
            <ThOrdenable columna="nro" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Documento
            </ThOrdenable>
            <ThOrdenable columna="proveedor_nombre" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Proveedor
            </ThOrdenable>
            <ThOrdenable columna="monto" orden={tabla.orden} onOrdenar={tabla.alternarOrden} alineado="derecha">
              Monto
            </ThOrdenable>
            <ThOrdenable columna="fecha_documento" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Fecha doc.
            </ThOrdenable>
            <ThOrdenable columna="status" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Estado
            </ThOrdenable>
            <ThOrdenable columna="comprador_asignado" orden={tabla.orden} onOrdenar={tabla.alternarOrden}>
              Aprobador
            </ThOrdenable>
            <th>Detalle del error</th>
            <th>Documentos</th>
          </tr>
        </thead>
        <tbody>
          {tabla.filas.length === 0 && (
            <tr>
              <td colSpan={COLUMNAS} className="empty-row">
                {items.length === 0
                  ? "No hay facturas en este filtro."
                  : itemsFiltrados.length === 0
                    ? "Ninguna factura tiene este estado."
                    : "Ninguna factura coincide con la busqueda."}
              </td>
            </tr>
          )}
          {tabla.filas.map((item) => (
            <Fragment key={item.id}>
              <tr>
                <td>
                  <CfeIdentidad
                    tipoCfe={item.tipo_cfe}
                    serie={item.serie}
                    nro={item.nro}
                    rutEmisor={item.rut_emisor}
                  />
                </td>
                <td className="wrap-ok" title={item.proveedor_nombre ?? undefined}>
                  {item.proveedor_nombre}
                </td>
                <td className="mono celda-monto">
                  {formatMonto(item.monto)}
                  <span className="moneda">{item.moneda}</span>
                </td>
                <td className="mono">{formatFecha(item.fecha_documento)}</td>
                <td>
                  <StatusBadge status={item.status} />
                  {item.status === "EN_SAP" && item.reviewed_by === null && (
                    <span
                      className="badge badge-wait badge-reconciliacion"
                      title="No pasó por el flujo de aprobación de este sistema — probablemente insertada en SAP por fuera, detectada por la reconciliación automática"
                    >
                      Detectado por reconciliación
                    </span>
                  )}
                </td>
                <td className="wrap-ok" title={item.comprador_asignado ?? undefined}>
                  {item.comprador_asignado ? (
                    <span className="mono">{item.comprador_asignado.split("@")[0]}</span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="error-text">{item.error_posteo ?? "—"}</td>
                <td className="actions-cell">
                  <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "xml")}>
                    XML
                  </button>
                  {item.pdf_blob_url && (
                    <button className="btn-ghost" onClick={() => abrirDocumento(item.id, "pdf")}>
                      PDF
                    </button>
                  )}
                  {/* [NUEVO 09/09/2026] Comprobante adicional que subió
                      Contaduría o el Aprobador en la fila de Notas (Pasos 1/2)
                      — hasta ahora se guardaba en Blob Storage pero no había
                      forma de verlo una vez que la factura avanzaba de paso.
                      Ni Contaduría ni el Aprobador pueden subir uno nuevo
                      desde acá (el backend ya lo bloquea: el gate de
                      /adjunto es por estado, solo PENDIENTE_CONTADURIA/
                      PENDIENTE_COMPRADOR) — acá es de solo lectura. */}
                  {item.adjunto_blob_url && (
                    <button
                      className="btn-ghost"
                      title={item.adjunto_nombre_original ?? undefined}
                      onClick={() => abrirDocumento(item.id, "adjunto")}
                    >
                      Adjunto
                    </button>
                  )}
                  <LineasToggle expanded={expandidos.has(item.id)} onToggle={() => toggleExpandido(item.id)} />
                  {/* [FUSIONADO 10/09/2026] "Acciones" vivía en su propia
                      columna, casi siempre vacía: Reintentar solo aparece en
                      ERROR_POSTEO (6 de 943 filas al momento de este cambio).
                      Mismo síntoma que en Rechazadas — sin borde vertical
                      entre celdas angostas, "Documentos" y "Acciones" se
                      veían como una sola, y encima acá "Acciones" quedaba
                      vacía en el 99% de los casos. Reportado por Liber
                      mirando la pantalla real. */}
                  {item.status === "ERROR_POSTEO" && (
                    <button disabled={accion.enCurso} onClick={() => reintentar(item.id)}>
                      Reintentar
                    </button>
                  )}
                </td>
              </tr>
              {expandidos.has(item.id) && <LineasRow reviewItemId={item.id} colSpan={COLUMNAS} />}
            </Fragment>
          ))}
        </tbody>
      </table>

      <Paginador
        pagina={tabla.pagina}
        totalPaginas={tabla.totalPaginas}
        desde={tabla.desde}
        hasta={tabla.hasta}
        total={tabla.total}
        onIrAPagina={tabla.irAPagina}
      />
    </div>
  );
}
